import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Modal } from '@mui/material';
import {
  listConnections, createConnection, updateConnection, deleteConnection, testConnection,
} from '../../api/networkDriveAPI';
import { useNotification } from '../../context/NotificationContext';
import { NotificationType } from '../../enums/NotificationType';
import ConfirmDeleteModal from '../shared/ConfirmDeleteModal';

// Zdieľaný disk = UNC cesta. Prístup beží pod účtom backendu (servisný účet) — bez hesla v aplikácii.
const emptyForm = { Name: '', RootPath: '', Enabled: true };

const ConnectionsList = () => {
  const [t] = useTranslation('global');
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [connections, setConnections] = useState([]);
  const [testing, setTesting] = useState({});
  const [testResult, setTestResult] = useState({});
  const [openModal, setOpenModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [modalTest, setModalTest] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const fetchConnections = () => {
    listConnections().then(d => setConnections(d || [])).catch(() => {});
  };
  useEffect(() => { fetchConnections(); }, []);

  const openAdd = () => { setEditId(null); setForm(emptyForm); setModalTest(null); setOpenModal(true); };
  const openEdit = (c) => {
    setEditId(c.Id);
    setForm({ Name: c.Name || '', RootPath: c.RootPath || '', Enabled: c.Enabled !== false });
    setModalTest(null);
    setOpenModal(true);
  };
  const closeModal = () => { if (!saving) setOpenModal(false); };

  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(current => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  const doTest = (id) => testConnection(id).catch(e => ({
    ok: false, detail: e?.response?.data?.detail || String(e),
  }));

  const runTest = async (id) => {
    setTesting(s => ({ ...s, [id]: true }));
    const r = await doTest(id);
    setTestResult(s => ({ ...s, [id]: r }));
    setTesting(s => ({ ...s, [id]: false }));
  };

  const runModalTest = async () => {
    if (editId === null) return;
    setModalTest('loading');
    setModalTest(await doTest(editId));
  };

  const handleSave = async () => {
    if (!form.RootPath.trim()) {
      showNotification({ message: t('fillRequired') || 'Vyplňte povinné polia', type: NotificationType.Error });
      return;
    }
    setSaving(true);
    try {
      const payload = { Name: form.Name, RootPath: form.RootPath, Enabled: !!form.Enabled };
      const saved = editId === null ? await createConnection(payload) : await updateConnection(editId, payload);
      showNotification({ message: t('savedChanges'), type: NotificationType.Success });
      setOpenModal(false);
      fetchConnections();
      if (saved?.Id) runTest(saved.Id);
    } catch (e) {
      showNotification({ message: e?.response?.data?.detail || t('errorOccured'), type: NotificationType.Error });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteConnection(toDelete.Id);
      showNotification({ message: t('savedChanges'), type: NotificationType.Success });
      fetchConnections();
    } catch {
      showNotification({ message: t('errorOccured'), type: NotificationType.Error });
    } finally { setToDelete(null); }
  };

  const renderTestBadge = (r, busy) => {
    if (busy || r === 'loading') return <span className="sp-badge sp-badge-muted">…</span>;
    if (!r) return null;
    return (
      <span className={`sp-badge ${r.ok ? 'sp-badge-ok' : 'sp-badge-err'}`}>
        <span className="material-symbols-outlined">{r.ok ? 'check' : 'error'}</span>
        {r.ok ? (t('driveAccessible') || 'Disk je dostupný') : (t('connectionError') || 'Chyba pripojenia')}
      </span>
    );
  };

  return (
    <div className="sp-card sp-card-flat">
      <div className="sp-card-head">
        <div className="sp-conn-head-left">
          <h3>{t('networkDrives') || 'Zdieľané disky'}</h3>
          {connections.length > 0 && <span className="sp-conn-count">{connections.length}</span>}
        </div>
        <button className="submit-button sp-btn-compact" onClick={openAdd}>
          <span className="material-symbols-outlined">add</span>&nbsp;{t('addNetworkDrive') || 'Pripojiť disk'}
        </button>
      </div>

      {connections.length > 0 && (
        <div className="sp-conn-summary">
          {connections.length} {t('networkDrivesLc') || 'diskov'} · {connections.reduce((s, c) => s + (c.FoldersCount || 0), 0)} {t('foldersCount') || 'priečinkov'}
        </div>
      )}

      {connections.length === 0 && (
        <div className="sp-invite">{t('noNetworkDrivesInvite') || 'Zatiaľ žiadne disky — pripoj prvý zdieľaný disk.'}</div>
      )}

      <div className="sp-conn-list">
        {connections.map(c => {
          const r = testResult[c.Id];
          const go = () => navigate('/NetworkDrive/connection/' + c.Id);
          return (
            <div
              key={c.Id}
              className={`sp-conn-card${c.Enabled === false ? ' disabled' : ''}`}
              onClick={go}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } }}
            >
              <div className="sp-conn-main">
                <span className={`material-symbols-outlined sp-conn-ico${c.Enabled !== false ? ' on' : ' off'}`}>
                  {c.Enabled !== false ? 'folder_shared' : 'folder_off'}
                </span>
                <div className="sp-conn-info">
                  <div className="sp-conn-name">{c.Name || c.RootPath}</div>
                  <div className="sp-conn-url" title={c.RootPath}>{c.RootPath}</div>
                  <div className="sp-conn-badges">
                    <span className={`sp-status ${c.Enabled !== false ? 'on' : 'off'}`}>
                      {c.Enabled !== false ? (t('enabled') || 'Zapnuté') : (t('disabled') || 'Vypnuté')}
                    </span>
                    {c.FoldersCount > 0 && (
                      <span className="sp-folder-chip">
                        <span className="material-symbols-outlined">folder</span>
                        {c.FoldersCount} {t('foldersCount') || 'priečinkov'}
                      </span>
                    )}
                    {renderTestBadge(r, testing[c.Id])}
                  </div>
                </div>
                <div className="sp-conn-actions" onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="sp-icon-btn" onClick={() => runTest(c.Id)} disabled={testing[c.Id]}
                    title={t('testConnection') || 'Test pripojenia'} aria-label={t('testConnection') || 'Test pripojenia'}>
                    <span className="material-symbols-outlined">wifi_tethering</span>
                  </button>
                  <button type="button" className="sp-icon-btn" onClick={() => openEdit(c)} title={t('edit')} aria-label={t('edit')}>
                    <span className="material-symbols-outlined">edit</span>
                  </button>
                  <button type="button" className="sp-icon-btn danger" onClick={() => setToDelete(c)} title={t('delete')} aria-label={t('delete')}>
                    <span className="material-symbols-outlined">delete</span>
                  </button>
                </div>
                <span className="sp-conn-chevron material-symbols-outlined" aria-hidden="true">chevron_right</span>
              </div>

              {r && !r.ok && (
                <div className="sp-errbox" onClick={(e) => e.stopPropagation()}>
                  <span className="material-symbols-outlined">error</span>
                  <div>
                    <div className="sp-errbox-title">{t('connectionError') || 'Chyba pripojenia'}</div>
                    <div className="sp-errbox-detail">{r.detail}</div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Modal open={openModal} onClose={closeModal} aria-labelledby="nd-conn-modal-title">
        <div className="env-modal sp-conn-modal">
          <div className="new-env-title">
            <h1 id="nd-conn-modal-title">
              {editId === null ? (t('addNetworkDrive') || 'Pripojiť disk') : (t('editNetworkDrive') || 'Upraviť disk')}
            </h1>
          </div>
          <form className="new-env-input-form" autoComplete="off" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
            <div className="input-line"><label>{t('name') || 'Názov'}</label>
              <input type="text" name="Name" value={form.Name} onChange={change} autoComplete="off" /></div>
            <div className="input-line"><label>{t('uncPath') || 'Cesta k zdieľanému disku'}</label>
              <input type="text" name="RootPath" value={form.RootPath} onChange={change} placeholder={t('uncPathPlaceholder')} />
              <span className="sp-hint">
                {t('uncPathHint') || 'Celý server \\\\server (zdieľania vyberiete v strome priečinkov) alebo jedno zdieľanie \\\\server\\zdielanie. Aplikácia pristupuje na disk pod svojím servisným účtom, ktorý musí mať právo na čítanie.'}
              </span></div>
            <div className="input-line ad-group-toggle-row"><label>{t('enabled') || 'Povolené'}</label>
              <label className="switch"><input type="checkbox" name="Enabled" checked={!!form.Enabled} onChange={change} /><span className="slider"></span></label></div>

            {editId !== null && (
              <div className="input-line sp-modal-test">
                <button type="button" className="download-button sp-test-btn" onClick={runModalTest}>
                  <span className="material-symbols-outlined">wifi_tethering</span>&nbsp;{t('testConnection') || 'Test pripojenia'}
                </button>
                {modalTest && (
                  <div className="sp-modal-test-result">
                    {renderTestBadge(modalTest, false)}
                    {modalTest !== 'loading' && !modalTest.ok && (
                      <div className="sp-errbox"><span className="material-symbols-outlined">error</span><div className="sp-errbox-detail">{modalTest.detail}</div></div>
                    )}
                  </div>
                )}
              </div>
            )}
          </form>
          <div className="sp-modal-footer">
            <button type="button" className="return-button" disabled={saving} onClick={closeModal}>{t('cancel')}</button>
            <button type="button" className="submit-button" disabled={saving} onClick={handleSave}>{t('save')}</button>
          </div>
        </div>
      </Modal>

      <ConfirmDeleteModal
        open={toDelete !== null}
        onClose={() => setToDelete(null)}
        onNo={() => setToDelete(null)}
        onYes={handleDelete}
        item={toDelete ? (toDelete.Name || toDelete.RootPath) : ''}
      />
    </div>
  );
};

export default ConnectionsList;
